// The member view's Accountability log tab (docs/mastermind-admin-plan.md 4.6 and 4.7): the same document
// the member sees, drawn in the shell's look, with Adam's tools above it, never printed. Before you send
// it lists only what Adam can fix; Print or save as PDF keeps a copy first (hub.note_log_saved) and then
// opens the print box; Send emails the member a link to their log in the hub (send-log-link), offered
// while they can open their mastermind's pages, and keeps a copy too. Each copy kept opens as it was
// (hub.accountability_copy), headed with the day and time. The Send box opens in place, under Before you send it, so
// what's left to fix is read first; ?send=1 (the members list's Send the log, the critique's admin 9) opens it.
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { setTitle } from '../../config/site.js'
import { Button, Field, Loading, Problem, TextArea, adminPath, useAdmin, useLoad } from '../ui/index.js'
import { AccountabilityLog, usePrintClass } from '../../products/mastermind/Log.jsx'
import { dm, hm, dmy } from '../../products/mastermind/log.js'
import { LOG } from '../../products/mastermind/logWords.js'
import { MM } from './words.js'
import { loadCopy, noteSaved, sendLink } from './data.js'
import { beforeSend } from './section.js'
import { Said } from './ThisMonth.jsx'

const TARGET = { sessions: '#mm-sessions', month: '#mm-this-month', goal: '#mm-goal' }

function SendBox({ first, person, product, onClose, onSent }) {
  const { api } = useAdmin()
  const [line, setLine] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const id = useId()
  const box = useRef(null)
  const tooLong = line.trim().length > 500
  useEffect(() => { box.current?.querySelector('textarea')?.focus() }, [])
  async function go() {
    if (busy || tooLong) return
    setBusy(true); setProblem(null)
    try {
      const answer = await sendLink(api, { person, product, line })
      await onSent(answer)
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }
  return (
    <section className="amx-send" role="dialog" aria-modal="false" aria-labelledby={`${id}-h`} ref={box}>
      <h3 className="amx-h3" id={`${id}-h`}>{MM.sendTitle(first)}</h3>
      <p className="amx-lede">{MM.sendLede(first)}</p>
      <Field label={MM.lineField} error={problem || (tooLong ? MM.lineTooLong : null)}>
        {props => <TextArea {...props} value={line} onChange={v => { setLine(v); setProblem(null) }} rows={3} maxLength={600} placeholder={MM.linePlaceholder} />}
      </Field>
      <span className="amx-row-actions">
        <Button kind="ink" busy={busy} disabled={tooLong} onClick={go}>{MM.sendYes}</Button>
        <Button onClick={onClose} disabled={busy}>{MM.sendNo}</Button>
      </span>
    </section>
  )
}

// 1 copy kept, as it was. sessions: the product's, so a copy kept during a session says it's reviewed at that session
// (log.js runningSession works it out from the copy's own moment, never the clock). They're today's sessions, not the
// ones of the day it was kept, so a copy whose session was later cancelled or moved names the document's own next one.
function CopyView({ eventId, back, first, sessions }) {
  const { api } = useAdmin()
  const copy = useLoad(() => loadCopy(api, Number(eventId)), [api, eventId])
  if (copy.status === 'loading' && !copy.data) return <Loading />
  if (copy.status === 'error') return <Problem error={copy.error} onRetry={copy.reload} />
  if (!copy.data?.log) return <p className="amx-muted">{MM.copyNotThere}</p>
  const tools = <Link className="ad-btn ad-btn--line ad-btn--sm" to={back}>{MM.backTo(first)}</Link>
  return <AccountabilityLog doc={copy.data.log} look="shell" tools={tools} asOf={LOG.asItWas(dmy(copy.data.at), hm(copy.data.at))} sessions={sessions} />
}

// sessions: the product's sessions Member.jsx reads beside the log (null while they load), for a session under way
export function LogTab({ log, product, first, query, onChanged, sessions = null }) {
  const { api } = useAdmin()
  const personId = log.person.person_id
  const productId = log.product.product_id
  const here = adminPath.mmLog(product.slug, personId)
  const member = adminPath.mmMember(product.slug, personId)
  const canSend = Boolean(log.access?.can_open)
  const [sending, setSending] = useState(() => query.get('send') === '1' && canSend)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  usePrintClass()
  useEffect(() => { setTitle(LOG.docTitle(log.person.full_name)) }, [log.person.full_name])

  const copyId = query.get('copy')
  if (copyId) return <CopyView eventId={copyId} back={here} first={first} sessions={sessions} />

  const copies = log.copies || []
  const lastSent = copies.find(c => c.kind === 'log_sent')
  const fixes = beforeSend(log, first)
  // a fix's link to This month keeps Adam's group and filter, never send=1 (the Send box is for this log only)
  const keep = new URLSearchParams(query)
  keep.delete('send')
  const qs = keep.toString() ? `?${keep}` : ''

  async function print() {
    if (busy) return
    setBusy(true); setLine(null)
    try {
      await noteSaved(api, { person: personId, product: productId }, { first, product: product.name })
      await onChanged()
      window.print()
    } catch {
      setLine({ bad: true, text: MM.printFailed })
    } finally {
      setBusy(false)
    }
  }

  const tools = (
    <div className="amx-logtools">
      <section className="amx-before" aria-labelledby="amx-before-h">
        <h2 className="amx-h3" id="amx-before-h">{MM.beforeH}</h2>
        {fixes.length
          ? <ul className="amx-before-list">{fixes.map(f => <li key={f.key}>{f.target ? <Link to={`${member}${qs}${TARGET[f.target] || ''}`}>{f.text}</Link> : f.text}</li>)}</ul>
          : <p className="amx-muted">{MM.allRecorded}</p>}
      </section>
      {sending && (
        <SendBox first={first} person={personId} product={productId} onClose={() => setSending(false)}
          onSent={async answer => { setSending(false); await onChanged(); setLine({ text: answer?.message || MM.sent(first, answer?.email || '') }) }} />
      )}
      <div className="amx-row-actions">
        <Button kind="ink" busy={busy} onClick={print}>{MM.print}</Button>
        <Button disabled={!canSend} aria-expanded={sending} onClick={() => { setSending(true); setLine(null) }}>{MM.send(first)}</Button>
      </div>
      <p className="amx-muted">{MM.printHint}</p>
      {!canSend && <p className="amx-muted">{MM.cantSend(first, product.name)}</p>}
      {lastSent && <p className="amx-meta">{MM.lastSent(dm(lastSent.at), hm(lastSent.at))}</p>}
      {copies.length > 0 && (
        <p className="amx-meta">
          {MM.copiesKeptLabel}{' '}
          {copies.map((c, i) => (
            <span key={c.event_id}>{i > 0 && '. '}<Link to={`${here}?copy=${c.event_id}`}>{MM.copyItem(c.kind, dm(c.at), hm(c.at))}</Link></span>
          ))}.
        </p>
      )}
      <Said line={line} />
    </div>
  )

  return <AccountabilityLog doc={log} look="shell" tools={tools} sessions={sessions} />
}
